import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRole, type Permission } from '@/hooks/use-role';
import { STATION_HOME_ROUTES } from '@/lib/roles';
import { Loader2 } from 'lucide-react';

type UserRole = 'Super Admin' | 'Admin' | 'Warehouse Coordinator' | 'Warehouse Staff' | 'Viewer' | 'Picker' | 'Printer' | 'Catcher' | 'Packer' | 'CX' | 'DTF Printer';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: Array<UserRole>;
  requireAdmin?: boolean;
  requirePurchaseOrderAccess?: boolean;
  requireReportingAccess?: boolean;
  /**
   * Platform-only surfaces (Manage Stores, help authoring): the caller must be on
   * the platform admin list (BLU-1313), not merely hold the 'Super Admin' role.
   */
  requirePlatformAdmin?: boolean;
  /**
   * The one permission the page needs (the RBAC four layers: sidebar, this
   * guard, the router procedure and the handler's `requirePermission` all
   * name it). `/suppliers*` uses `suppliers.view`.
   */
  requirePermission?: Permission;
  redirectTo?: string;
}

export default function RoleGuard({
  children,
  allowedRoles,
  requireAdmin,
  requirePurchaseOrderAccess,
  requireReportingAccess,
  requirePlatformAdmin,
  requirePermission,
  redirectTo = '/',
}: RoleGuardProps) {
  const { profile, loading } = useAuth();
  const { can, isPlatformAdmin } = useRole();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  // Permission-based checks
  if (requireAdmin && !can('org_settings.manage')) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requirePurchaseOrderAccess && !can('purchase_orders.view')) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requireReportingAccess && !can('reporting.view')) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requirePlatformAdmin && !isPlatformAdmin()) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requirePermission && !can(requirePermission)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check allowed roles list
  if (allowedRoles && !allowedRoles.includes(profile.role as UserRole)) {
    // Station users (and CX) redirect to their own home screen, not '/' —
    // for station roles that's obviously true, but CX needs it too: CX isn't
    // allowed on the workspace-toggle's default '/production/dashboard' or on
    // '/' itself (HomeRouteSwitch's RoleGuard also excludes CX), so falling
    // through to the default redirectTo='/' bounced CX in a self-referential
    // Navigate-to-'/' loop that never reached the sidebar. Send CX straight to
    // Tracker, the one page every CX build is guaranteed to be allowed on.
    const roleHomeRoutes: Record<string, string> = {
      ...STATION_HOME_ROUTES,
      CX: '/production/tracker',
      // Warehouse Staff has Tracker/Issues access but NOT the Production
      // Dashboard the workspace toggle lands on — without an entry here the
      // toggle bounced them straight back to '/'.
      'Warehouse Staff': '/production/tracker',
    };
    const destination = roleHomeRoutes[profile.role] ?? redirectTo;
    return <Navigate to={destination} replace />;
  }

  return <>{children}</>;
}